import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home, FileEdit, Layers, Wrench, Building2, FolderKanban, Handshake, MessageSquareQuote,
  Award, Newspaper, Image, Mail, Users, DatabaseBackup, LogOut, FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import HomepageEditor from "@/components/admin/HomepageEditor";
import PageEditor from "@/components/admin/PageEditor";
import ContentManager from "@/components/admin/ContentManager";
import BlogManager from "@/components/admin/BlogManager";
import EnquiriesTable from "@/components/admin/EnquiriesTable";
import MediaLibrary from "@/components/admin/MediaLibrary";
import BackupRestore from "@/components/admin/BackupRestore";

const LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_labpronics-tech/artifacts/a7d11asc_ChatGPT%20Image%20Aug%204%2C%202026%2C%2003_51_10%20AM.png";

const CONFIGS = {
  solutions: { title: "Solutions", fields: [
    { key: "title", label: "Title", type: "text" }, { key: "icon", label: "Icon", type: "text", hint: "Cctv, Radar, Server…" },
    { key: "image", label: "Image", type: "image" }, { key: "excerpt", label: "Excerpt", type: "textarea" },
    { key: "content", label: "Content", type: "richtext" }, { key: "status", label: "Status", type: "status" }],
    blank: { title: "", icon: "Layers", image: "", excerpt: "", content: "", status: "published" } },
  services: { title: "Services", fields: [
    { key: "title", label: "Title", type: "text" }, { key: "icon", label: "Icon", type: "text", hint: "Wrench, Headset…" },
    { key: "image", label: "Image", type: "image" }, { key: "excerpt", label: "Excerpt", type: "textarea" },
    { key: "content", label: "Content", type: "richtext" }, { key: "status", label: "Status", type: "status" }],
    blank: { title: "", icon: "Wrench", image: "", excerpt: "", content: "", status: "published" } },
  industries: { title: "Industries", fields: [
    { key: "name", label: "Name", type: "text" }, { key: "icon", label: "Icon", type: "text", hint: "Landmark, Shield…" },
    { key: "image", label: "Image", type: "image" }, { key: "description", label: "Description", type: "textarea" },
    { key: "status", label: "Status", type: "status" }],
    blank: { name: "", icon: "Landmark", image: "", description: "", status: "published" } },
  projects: { title: "Projects", fields: [
    { key: "title", label: "Title", type: "text" }, { key: "location", label: "Location", type: "text" },
    { key: "image", label: "Image", type: "image" }, { key: "excerpt", label: "Excerpt", type: "textarea" },
    { key: "content", label: "Content", type: "richtext" }, { key: "tags", label: "Tags", type: "tags" },
    { key: "status", label: "Status", type: "status" }],
    blank: { title: "", location: "", image: "", excerpt: "", content: "", tags: [], status: "published" } },
  oem_partners: { title: "OEM Partners", fields: [
    { key: "name", label: "Name", type: "text" }, { key: "logo", label: "Logo", type: "image" },
    { key: "category", label: "Category", type: "text" }, { key: "website", label: "Website", type: "text" },
    { key: "status", label: "Status", type: "status" }],
    blank: { name: "", logo: "", category: "", website: "", status: "published" } },
  testimonials: { title: "Testimonials", fields: [
    { key: "quote", label: "Quote", type: "textarea" }, { key: "name", label: "Name / Title", type: "text" },
    { key: "role", label: "Role", type: "text" }, { key: "org", label: "Organisation", type: "text" },
    { key: "status", label: "Status", type: "status" }],
    blank: { quote: "", name: "", role: "", org: "", status: "published" } },
  certifications: { title: "Certifications", fields: [
    { key: "name", label: "Name", type: "text" }, { key: "image", label: "Badge Image", type: "image" },
    { key: "description", label: "Description", type: "text" }, { key: "status", label: "Status", type: "status" }],
    blank: { name: "", image: "", description: "", status: "published" } },
};

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("homepage");

  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);
  if (!ready || !user) return <div className="min-h-screen bg-lp-grey" />;

  const isAdmin = user.role === "admin";
  const NAV = [
    { group: "Website" },
    ["homepage", "Homepage", Home], ["about-page", "About Page", FileEdit],
    { group: "Content" },
    ["solutions", "Solutions", Layers], ["services", "Services", Wrench], ["industries", "Industries", Building2],
    ["projects", "Projects", FolderKanban], ["oem_partners", "OEM Partners", Handshake],
    ["testimonials", "Testimonials", MessageSquareQuote], ["certifications", "Certifications", Award],
    ["blog", "Blog", Newspaper], ["media", "Media Library", Image],
    { group: "Operations" },
    ["enquiries", "Enquiries", Mail],
    ...(isAdmin ? [["users", "Users", Users], ["backup", "Backup & Restore", DatabaseBackup]] : []),
  ];

  return (
    <div className="min-h-screen bg-lp-grey flex">
      <aside className="w-60 bg-lp-blue text-white flex flex-col fixed h-full overflow-y-auto">
        <div className="p-5 flex items-center gap-3 border-b border-white/10 sticky top-0 bg-lp-blue">
          <img src={LOGO} alt="" className="h-9 w-9 bg-white rounded-full p-0.5" />
          <div><div className="font-heading font-700 text-sm">Admin Console</div><div className="text-[10px] text-lp-cyan uppercase tracking-wide">{user.role}</div></div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {NAV.map((item, i) => item.group ? (
            <div key={i} className="px-3 pt-4 pb-1 text-[10px] font-700 uppercase tracking-wider text-white/40">{item.group}</div>
          ) : (
            <button key={item[0]} onClick={() => setTab(item[0])} data-testid={`admin-tab-${item[0]}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-600 transition-colors ${tab === item[0] ? "bg-white/15 text-lp-cyan" : "text-white/75 hover:bg-white/10"}`}>
              {(() => { const I = item[2]; return <I size={17} />; })()} {item[1]}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="text-[11px] text-white/50 mb-2 px-2 truncate">{user.email}</div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-600 text-white/75 hover:bg-white/10"><LogOut size={17} /> Sign Out</button>
        </div>
      </aside>

      <main className="flex-1 ml-60 p-8">
        {tab === "homepage" && <HomepageEditor />}
        {tab === "about-page" && <PageEditor />}
        {tab === "blog" && <BlogManager />}
        {tab === "media" && <MediaLibrary />}
        {tab === "enquiries" && <EnquiriesTable />}
        {tab === "backup" && isAdmin && <BackupRestore />}
        {tab === "users" && isAdmin && <UsersTab />}
        {CONFIGS[tab] && <ContentManager collection={tab} title={CONFIGS[tab].title} fields={CONFIGS[tab].fields} blank={CONFIGS[tab].blank} />}
      </main>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h1 className="font-heading font-700 text-2xl text-lp-blue mb-6 flex items-center gap-2"><FileText size={22} className="text-lp-cyan" /> Users</h1>
      <div className="bg-white border border-lp-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-lp-grey text-left"><tr><th className="px-4 py-3 font-600">Name</th><th className="px-4 py-3 font-600">Email</th><th className="px-4 py-3 font-600">Role</th></tr></thead>
          <tbody>{users.map((u) => (<tr key={u.id} className="border-t border-lp-border"><td className="px-4 py-3 text-lp-ink font-600">{u.name}</td><td className="px-4 py-3 text-lp-muted">{u.email}</td><td className="px-4 py-3"><span className="text-xs font-600 bg-lp-cyan/15 text-lp-blue px-2 py-1 rounded-sm">{u.role}</span></td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
